import React, { useEffect, useState } from 'react';
import apiService from '../../services/api';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Chip,
  Stack,
  Avatar,
  Divider,
  TablePagination,
  TextField,
  Autocomplete,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import SearchIcon from '@mui/icons-material/Search';

// ===== Types =====
interface User {
  id: number;
  roleId: number;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
}

interface Query {
  id: number;
  userId: number;
  queryType: string;
  subject: string;
  description: string;
  users: User;
  createdAt: string;
}

const QUERY_TYPES = [
  'Technical Issue',
  'Account Related',
  'Meeting Issue',
  'Feature Request',
  'Other',
];

const QueryList: React.FC = () => {
  const [queries, setQueries] = useState<Query[]>([]);
  const [loading, setLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [expanded, setExpanded] = useState<number | false>(false);
  /* Pagination */
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  const [search, setSearch] = useState('');
  const [email, setEmail] = useState('');
  const [queryType, setQueryType] = useState<string | null>(null);

  // const fetchQueryList = async (isInitial = false) => {
  //   isInitial ? setLoading(true) : setListLoading(true);

  //   try {
  //     const response = await apiService.getAllQuery({
  //       page: page + 1,
  //       limit: rowsPerPage,
  //       searchTerm: search.trim() || undefined,
  //       queryType: queryType || undefined,
  //     });

  //     setQueries(response.data || []);
  //     setTotal(response.total || 0);
  //   } catch (err) {
  //     console.error('Failed to fetch queries', err);
  //   } finally {
  //     isInitial ? setLoading(false) : setListLoading(false);
  //   }
  // };
  const fetchQueryList = async (isInitial = false) => {
    isInitial ? setLoading(true) : setListLoading(true);

    try {
      const response = await apiService.getAllQuery({
        page: page + 1,
        limit: rowsPerPage,
        searchTerm: search.trim() || undefined,
        email: email.trim() || undefined,
        queryType: queryType || undefined,
      });

      setQueries(response.data || []);
      setTotal(response.total || 0);
    } catch (err) {
      console.error('Failed to fetch queries', err);
    } finally {
      isInitial ? setLoading(false) : setListLoading(false);
    }
  };

  useEffect(() => {
    fetchQueryList(true);
  }, []);

  useEffect(() => {
    fetchQueryList();
  }, [page, rowsPerPage, search, queryType, email]);

  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newLimit = parseInt(event.target.value, 10);
    setRowsPerPage(newLimit);
    setPage(0);
  };

  const handleChange = (id: number) => (_: any, isExpanded: boolean) => {
    setExpanded(isExpanded ? id : false);
  };

  return (
    <Box sx={{ p: 3, bgcolor: '#f9fafb', minHeight: '100vh' }}>
      <Box sx={{ mb: 3 }}>
        {/* Title + Pagination */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Typography variant="h5" fontWeight={600}>
            User Queries
          </Typography>

          {queries.length > 0 && (
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={{
                '& .MuiTablePagination-toolbar': {
                  minHeight: 40,
                  px: 0,
                },
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows':
                  {
                    fontSize: '0.8rem',
                  },
              }}
            />
          )}
        </Box>

        {/* Filters */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems="center"
        >
          {/* Query Type Filter */}
          <Autocomplete
            size="small"
            options={QUERY_TYPES}
            value={queryType}
            onChange={(_, value) => {
              setQueryType(value);
              setPage(0);
            }}
            renderInput={params => (
              <TextField {...params} placeholder="Filter by Query Type" />
            )}
            sx={{ width: { xs: '100%', sm: 240 } }}
            clearOnEscape
          />

          {/* Search */}
          <TextField
            size="small"
            placeholder="Search by subject or description"
            value={search}
            onChange={e => {
              setSearch(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ mr: 1, color: 'text.disabled' }} />
              ),
            }}
            sx={{ width: { xs: '100%', sm: 260 } }}
          />

          {/* Search by email */}
          <TextField
            size="small"
            placeholder="Search by email"
            value={email}
            onChange={e => {
              setEmail(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ mr: 1, color: 'text.disabled' }} />
              ),
            }}
            sx={{ width: { xs: '100%', sm: 260 } }}
          />
        </Stack>
      </Box>

      {listLoading && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            py: 4,
          }}
        >
          <Typography color="text.secondary">Loading results…</Typography>
        </Box>
      )}

      {!listLoading && queries.length > 0 && (
        <Stack spacing={2}>
          {queries.map(query => (
            <Accordion
              key={query.id}
              expanded={expanded === query.id}
              onChange={handleChange(query.id)}
              sx={{
                borderRadius: 2,
                '&:before': { display: 'none' },
                boxShadow: '0 4px 14px rgba(0,0,0,0.08)',
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ px: { xs: 2, md: 3 }, py: 2 }}
              >
                <Stack
                  direction="row"
                  spacing={2}
                  width="100%"
                  alignItems="center"
                >
                  {/* Left */}
                  <Stack spacing={0.5} flex={1}>
                    <Typography fontWeight={500}>
                      Subject: {query.subject}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Submitted by {query.users.firstName}{' '}
                      {query.users.lastName}
                    </Typography>
                  </Stack>

                  {/* Right */}
                  <Chip
                    label={query.queryType}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </Stack>
              </AccordionSummary>

              <AccordionDetails sx={{ px: { xs: 2, md: 3 }, pb: 3 }}>
                <Divider sx={{ mb: 2 }} />

                <Stack spacing={3}>
                  {/* Description */}
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Description
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {query.description}
                    </Typography>
                  </Box>

                  {/* Meta info */}
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={4}
                    alignItems={{ sm: 'center' }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <EmailIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        Email: {query.users.email}
                      </Typography>
                    </Stack>

                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <PhoneIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        Phone: {query.users.phone || 'N/A'}
                      </Typography>
                    </Stack>

                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <AccessTimeIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        Created: {new Date(query.createdAt).toLocaleString()}
                      </Typography>
                    </Stack>
                  </Stack>
                </Stack>
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
      )}

      {/* Empty State */}
      {!loading && queries.length === 0 && (
        <Box
          sx={{
            mt: 6,
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            color: 'text.disabled',
          }}
        >
          <SearchOffIcon sx={{ fontSize: 48, mb: 1 }} />
          <Typography variant="h6" fontWeight={500}>
            No queries found
          </Typography>
          <Typography variant="body2">
            There are no user queries available at the moment.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default QueryList;
